/**
 * browserExecutionStateVocabulary — the one Hebrew vocabulary for the bounded browser fallback
 * session's own state (`ExecutionState`).
 *
 * The field has a single producer and a closed scale: `ExecutionState` in
 * `backend/app/browser_work/execution.py`, seven arms, and every write to it goes through the
 * stored sanitized execution rows (`browser_work_executor`), so no prose writer can put a word in
 * it the enum does not hold. What it had instead was one printer that held six of the seven arms —
 * `EmployeeWorkspace`'s execution chip — and read them with `EXECUTION_STATE_HE[state.state] ||
 * state.state`, so the seventh arm fell through the map and printed the producer's own English
 * identifier inside a Hebrew screen. The arm that was missing is the *first* one an operator meets:
 * `requested` is the state of a recorded request that has no execution record yet, which is what
 * `_latest_execution_state` returns by default (`services/browser_work_executor.py`) and what the
 * read-only state route documents outright — "Before any approval the state is ``requested``". So
 * the screen that exists to show an operator where a bounded session stands showed them the English
 * word for it, on the refresh the flow offers before anything has been approved.
 *
 * Two rules this module exists to hold:
 *
 *   1. One scale, re-derived from the producer — never a page-local subset.
 *      `browserExecutionStateVocabulary.test.js` parses the enum out of the Python source and
 *      asserts this map's key set EQUALS it, so a renamed arm or an eighth arm reddens the suite
 *      instead of shipping a raw token to an operator.
 *   2. An unrecognised value is NAMED, never echoed. A value outside the scale means this build is
 *      behind the producer; the operator needs a Hebrew word and a reason to look, not an English
 *      token on screen. The cost is that a producer rename reads as "unrecognised" until the map
 *      catches up — which is exactly what rule 1's pin is for.
 *
 * The terminal set is exported from here too, because the flow's "the session has ended, the same
 * request cannot be re-run" note and this map are two readings of one machine: the producer's
 * `EXECUTION_STATE_TERMINAL` frozenset. The test re-derives it the same way, so an arm that stops
 * being terminal stops being rendered as terminal.
 *
 * WHAT THIS MODULE DOES NOT OWN, each verified by reading the producer rather than assumed:
 *   - `BrowserWorkSessionPanel.STAGE_META` and `RemoteOperationsPage.SESSION_STAGE_HE` word a
 *     session's *stage*, not this state. `browser_work_sessions._session_record` always writes a
 *     `stage`, and `_EXECUTION_STAGE_BY_STATE` translates every arm of this enum into one of those
 *     stage words (`executing` → `running`, `blocked` → `execution_blocked`), so no arm of this
 *     enum can reach either chip unworded.
 *   - `BROWSER_REQUEST_STATE_HE` in `EmployeeWorkspace` words the *receipt's* state
 *     (`routers/agents.py` stamps `"recorded"`), and `BROWSER_ACTION_KIND_HE` words
 *     `BoundedActionKind` — two other closed scales.
 *   - The mono `dir="ltr"` echoes on the same strip (`settle`, `state.verification`, `receipt.mode`,
 *     `receipt.state`) are deliberate code echoes of recorded identifiers, not labels.
 */

export const BROWSER_EXECUTION_STATE_HE = {
  // The recorded request has no execution record yet, and `ALLOWED_TRANSITIONS` lets `requested`
  // leave only to `approved` (or to `blocked`) — so "not yet approved" is what the machine itself
  // guarantees at this arm, and it is the word the receipt chip beside it already uses for the same
  // request ("נרשמה").
  requested:               "נרשמה — טרם אושרה",
  approved:                "אושר — ממתין להתחלה",
  executing:               "בביצוע",
  waiting_for_credential:  "ממתין למסירת אישורי התחברות",
  verified:                "אומת",
  failed:                  "נכשל",
  blocked:                 "נחסם",
};

/** Shown for a value the scale does not hold, and for an absent one. Never the identifier itself. */
export const BROWSER_EXECUTION_STATE_UNKNOWN_HE = "מצב לא מזוהה";

/**
 * The arms that end a session: the producer's `EXECUTION_STATE_TERMINAL`
 * (`execution.py`), which is `frozenset({VERIFIED, FAILED, BLOCKED})`.
 */
export const BROWSER_EXECUTION_TERMINAL_STATES = ["verified", "failed", "blocked"];

/**
 * The Hebrew word for a stored execution state. `hasOwnProperty` rather than
 * `BROWSER_EXECUTION_STATE_HE[value] ||`: a bare lookup answers an inherited key (`"constructor"`,
 * `"toString"`) with a function, which the `||` fallback would treat as a hit and render.
 */
export function browserExecutionStateLabel(value) {
  return Object.prototype.hasOwnProperty.call(BROWSER_EXECUTION_STATE_HE, value)
    ? BROWSER_EXECUTION_STATE_HE[value]
    : BROWSER_EXECUTION_STATE_UNKNOWN_HE;
}
